import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { Box, Button, Chip, CircularProgress, Divider, Paper, Typography } from '@mui/material';
import { componentLogger } from '@ragu-components/logger';
import { trackAnalyticsEvent } from '@ragu-components/ord-analytics';
import { isoToDateTime } from '@ragu-components/ord-views';
import { canAccessWithPermissions } from '@ratto-components/ra-rbac';
import {
  NumberInput,
  SimpleForm,
  maxValue,
  minValue,
  useGetOne,
  useLocale,
  useNotify,
  usePermissions,
  useTranslate,
} from 'react-admin';

import { useIngordDataProvider } from 'src/components/flowchart/hooks/useIngordDataProvider';

import type { AutoprovisioningAuthentication } from '@ragu-components/ord-types';
import type { FieldValues } from 'react-hook-form';

const DEFAULT_DURATION = 3600;

export const ApAuthenticationPage = (): React.ReactElement => {
  const dataProvider = useIngordDataProvider();
  const { permissions } = usePermissions();
  const canList = canAccessWithPermissions({ permissions, action: 'list', resource: 'ap_authentication' });
  const canCreate = canAccessWithPermissions({ permissions, action: 'create', resource: 'ap_authentication' });
  const notify = useNotify();
  const translate = useTranslate();
  const locale = useLocale();
  const [saving, setSaving] = useState(false);

  const {
    data: auth,
    isLoading,
    error,
    refetch,
  } = useGetOne<AutoprovisioningAuthentication & { id: string }>(
    'autoprovisioning/authentication',
    { id: '' },
    { enabled: canList }
  );

  useEffect(() => {
    if (error) {
      componentLogger.error('Autoprovisioning authentication fetch failed', { error });
      notify('resources.autoprovisioning.authentication.notifications.fetchError', { type: 'error' });
    }
  }, [error, notify]);

  useEffect(() => {
    if (auth?.enabled !== false || !auth?.expires_at) return;
    const msUntilExpiry = new Date(auth.expires_at).getTime() - Date.now();
    if (msUntilExpiry <= 0) {
      void refetch();
      return;
    }
    const timer = setTimeout(() => {
      void refetch();
    }, msUntilExpiry);
    return () => {
      clearTimeout(timer);
    };
  }, [auth?.enabled, auth?.expires_at, refetch]);

  const handleDisable = useCallback(
    async (values: FieldValues) => {
      setSaving(true);
      try {
        await dataProvider.updateAutoprovisioningAuthentication({
          enabled: false,
          duration: values.duration as number,
        });
        await refetch();
        componentLogger.info('Autoprovisioning authentication disabled');
        trackAnalyticsEvent('autoprovisioning_auth_disabled', { event_category: 'autoprovisioning' });
        notify('resources.autoprovisioning.authentication.notifications.disableSuccess', { type: 'info' });
      } catch (error) {
        componentLogger.error('Autoprovisioning authentication disable failed', { error });
        trackAnalyticsEvent('autoprovisioning_auth_disable_failed', { event_category: 'autoprovisioning' });
        notify('resources.autoprovisioning.authentication.notifications.disableError', { type: 'error' });
      } finally {
        setSaving(false);
      }
    },
    [dataProvider, refetch, notify]
  );

  const handleEnable = useCallback(async () => {
    setSaving(true);
    try {
      await dataProvider.updateAutoprovisioningAuthentication({ enabled: true });
      await refetch();
      componentLogger.info('Autoprovisioning authentication enabled');
      trackAnalyticsEvent('autoprovisioning_auth_enabled', { event_category: 'autoprovisioning' });
      notify('resources.autoprovisioning.authentication.notifications.enableSuccess', { type: 'info' });
    } catch (error) {
      componentLogger.error('Autoprovisioning authentication enable failed', { error });
      trackAnalyticsEvent('autoprovisioning_auth_enable_failed', { event_category: 'autoprovisioning' });
      notify('resources.autoprovisioning.authentication.notifications.enableError', { type: 'error' });
    } finally {
      setSaving(false);
    }
  }, [dataProvider, refetch, notify]);

  if (isLoading && canList) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const isExpired = auth?.expires_at != null && new Date(auth.expires_at) < new Date();
  const isDisabled = auth != null && !auth.enabled && !isExpired;

  return (
    <Paper sx={{ overflow: 'hidden', mt: 1.5 }}>
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant='h6'>{translate('resources.autoprovisioning.authentication.name')}</Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
          {translate('resources.autoprovisioning.authentication.description')}
        </Typography>
      </Box>

      <Divider />

      <SimpleForm onSubmit={handleDisable} defaultValues={{ duration: DEFAULT_DURATION }} toolbar={false}>
        {/* Status row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant='body2'>
            {translate('resources.autoprovisioning.authentication.fields.enabled')}
          </Typography>
          <Chip
            label={translate(
              isDisabled
                ? 'resources.autoprovisioning.authentication.status.disabled'
                : 'resources.autoprovisioning.authentication.status.enabled'
            )}
            color={isDisabled ? 'error' : 'success'}
            size='small'
          />
          {isDisabled && auth?.expires_at != null && (
            <Typography variant='body2' color='text.secondary'>
              {translate('resources.autoprovisioning.authentication.fields.expires_at')}:{' '}
              {isoToDateTime(auth.expires_at, locale, false)}
            </Typography>
          )}
        </Box>

        {isDisabled && canCreate && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant='contained'
              color='success'
              disabled={saving}
              onClick={() => {
                void handleEnable();
              }}
            >
              {translate('resources.autoprovisioning.authentication.actions.enable')}
            </Button>
          </Box>
        )}

        {/* Disable section — only when auth is currently enabled */}
        {!isDisabled && canCreate && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 1 }}>
              {translate('resources.autoprovisioning.authentication.disableSection')}
            </Typography>
            <NumberInput
              source='duration'
              label='resources.autoprovisioning.authentication.fields.duration'
              required={true}
              disabled={saving}
              fullWidth
              validate={[minValue(60), maxValue(86400)]}
            />
            <Button type='submit' variant='contained' color='error' disabled={saving}>
              {translate('resources.autoprovisioning.authentication.actions.disable')}
            </Button>
          </Box>
        )}
      </SimpleForm>
    </Paper>
  );
};
